import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import {Customer} from '../model/customer';
import {ICustomer} from '../views/customers/customer';
import {GeneralService} from './general.service';
import {StoreService} from './store.service';
import {EDIServices} from './edi.services';
import {IResponse} from '../model/server_response';

@Injectable()
export class CustomerService extends GeneralService implements EDIServices<Observable<ICustomer>, ICustomer, number> {

    private store: StoreService;

    constructor(protected http: HttpClient) {
        super(http);

        this.store = new StoreService('customers', 'CU1_ID');
    }

    getById(id: number, CO1_ID: number): Observable<ICustomer> {

        if (id === 0) {
            return Observable.of(this.initialize());
        }

        const url = `${this.apiUrl}/customers/${id}/${CO1_ID}`;

        return this.http.get(url)
            .map(
                data => this.extractPayload(data)
            )
            .catch(this.handleError);
    }

    save(customer: ICustomer, isCreate: boolean): Observable<ICustomer> {
        if (customer.CU1_ID === 0 || isCreate) {
            return this.create(customer);
        }
        return this.update(customer);
    }

    merge( data ): Observable<IResponse<any>> {
        return this.http.post(this.apiUrl + '/customers/merge', data)
            .catch(this.handleError);
    }

    public load(): Observable<IResponse<Customer>> {
        return this.http.get<IResponse<Customer>>(this.apiUrl + '/customers');
    }

    public getCustomers(page): Observable<IResponse<Customer>> {
        const current_page = page.pageNumber + 1;
        let params = new HttpParams();
        params = params.append('perPage', page.size);
        params = params.append('page', current_page);
        if (!_.isUndefined(page.sortParams)) {
            for (const key of Object.keys(page.sortParams)) {
                const value = page.sortParams[key];
                params = params.append('sort[]', key + ',' + value );
            }
        }
        if (!_.isUndefined(page.filterParams)) {
            for (const key of Object.keys(page.filterParams)) {
                const value = page.filterParams[key];
                if (value && value !== '') {
                    params = params.append(key, value);
                }
            }
        }
        return this.http.get<IResponse<Customer>>(this.apiUrl + '/customers', { params : params });
    }

    private create(customer: ICustomer): Observable<ICustomer> {
        return this.http.post(this.apiUrl + '/customers/', customer)
            .do(data => console.log('createCustomer: ' + JSON.stringify(data)))
            .catch(this.handleError);
    }

    private update(customer: ICustomer): Observable<ICustomer> {
        const url = `${this.apiUrl + '/customers'}/${customer.CU1_ID}`;
        return this.http.put(url, customer)
            .catch(this.handleError);
    }

    public delete(id: number) {
        return this.http.delete<any>(this.apiUrl + '/customers/' + id);
    }

    public getAccess(id: number, CO1_ID: number) {
        const url = `${this.apiUrl + '/customers/access'}/${id}/${CO1_ID}`;
        return this.http.get(url)
            .catch(this.handleError);
    }

    public setAccess(id: number, CO1_ID: number, data: any) {
        const url = `${this.apiUrl + '/customers/access'}/${id}/${CO1_ID}`;
        return this.http.put(url, data)
            .catch(this.handleError);
    }

    private initialize(): ICustomer {
        // Return an initialized object
        return {
            'CU1_ID': 0,
            'CU0_ID': 0,
            'CORP_ADDRESS_ID': null,
            'CU1_NAME': null,
            'CU1_CREATED_BY': null,
            'CU1_CREATED_ON': null,
            'CU1_MODIFIED_BY': null,
            'CU1_MODIFIED_ON': null,
            'CU1_SHOW_DEFAULT': null,
            'CU1_TEST_MODE': null,
            'CU1_COMPANY_ID': null,
            'CU1_RECEIVE_EDI': null,
            'CU1_SEND_EDI_INVOICES': null,
            'CU1_SEND_EDI_ASN': null,
            'CU1_SEND_EDI_ORDERS': null,
            'CU1_SEND_EDI_ORDER_CONFIRMATIONS': null,
            'CU1_SEND_ACKNOWLEDGEMENT': null,
            'CU1_ORDER_TYPE': null,
            'CU1_ORDER_FORMAT': null,
            'CU1_INVOICE_FORMAT': null,
            'CU1_ASN_FORMAT': null,
            'CU1_TXT_APPROVED': null,
            'CU1_SEND_FTP': null,
            'CU1_SEND_SFTP': null,
            'CU1_POST_HTTP': null,
            'CU1_RECEIVE_FTP': null,
            'CU1_PICKUP_FTP': null,
            'CU1_RECEIVE_HTTP': null,
            'CU1_PICKUP_SFTP': null,
            'CU1_REMOTE_FTP_SERVER': null,
            'CU1_REMOTE_FTP_USERNAME': null,
            'CU1_REMOTE_FTP_PASSWORD': null,
            'CU1_REMOTE_FTP_DIRECTORY_SEND': null,
            'CU1_REMOTE_FTP_DIRECTORY_PICKUP': null,
            'CU1_FTP_USER': null,
            'CU1_FTP_PASSWORD': null,
            'CU1_FTP_DIRECTORY': null,
            'CU1_REMOTE_HTTP_SERVER': null,
            'CU1_REMOTE_HTTP_USERNAME': null,
            'CU1_REMOTE_HTTP_PASSWORD': null,
            'CU1_SUPPLIER_CODE': null,
            'CU1_RECEIVER_QUALIFIER': null,
            'CU1_RECEIVER_ID': null,
            'CU1_FACILITY': null,
            'CU1_TRADING_PARTNER_QUALIFIER': null,
            'CU1_TRADING_PARTNER_ID': null,
            'CU1_TRADING_PARTNER_GS_ID': null,
            'CU1_ASN_TRADING_PARTNER_ID': null,
            'CU1_CONSOLIDATE_ASN': null,
            'CU1_FLAG': null,
            'CU1_X12_STANDARD': null,
            'CU1_EDI_VERSION': null,
            'CU1_DUNS': null,
            'CU1_SHARED_SECRET': null,
            'CU1_REJECT_INVALID_ITEM_ORDERS': null,
            'CU1_INVALID_ITEM_SUBSTITUTE': null,
            'CU1_USE_CONTRACT': null,
            'CU1_SEND_CUSTOMERS_AND_ITEMS': null,
            'CU1_STOP_IMPORT_WITH_ERRORS': null,
            'CU1_USE_CLASS_ID': null,
            'CU1_CLASS_ID': null,
            'CU1_MAP': null,
            'CU1_ORDER_PRICE_OVERRIDE': null,
            'CU1_SEND_CREDIT_INVOICES': null,
            'CU1_ONLY_SEND_CREDIT_INVOICES': null,
            'CU1_SEND_PAID_INVOICES': null,
            'CU1_852_IMPORT_FOLDER': null,
            'CU1_ALWAYS_SEND_ORDER_CONFIRMATIONS': null,
            'CU1_COMPLETE_SHIP_TO_NAME': null,
            'CU1_ALWAYS_SEND_ASNS': null,
            'CU1_IMPORT_FREIGHT_CODES': null,
            'CU1_POST_AS2': null,
            'CU1_RECEIVE_AS2': null,
            'CU1_CXML_PAYLOAD_ID': null,
            'CU1_AS2_CERTIFICATE_FILENAME': null,
            'CU1_AS2_RECEIVER_ID': null,
            'CU1_AS2_TRADING_PARTNER_ID': null,
            'CU1_USE_P21_SHIP_TO_DATA': null,
            'CU1_CUSTOMER_SENDS_P21_SHIP_TO_ID': null,
            'CU1_ALLOW_DUPLICATE_PO_NUMBERS': null,
            'CU1_ASN_IMPORT_FOLDER': null,
            'CU1_PICKUP_DIRECTORY': null,
            'CU1_SEND_INVENTORY': null,
            'CU1_INVENTORY_FORMAT': null,
            'CU1_AS2_REQUEST_RECEIPT': null,
            'CU1_AS2_SIGN_MESSAGES': null,
            'CU1_AS2_KEY_LENGTH': null,
            'CU1_AS2_ENCRYPTION_ALGORITHM': null,
            'CU1_AS2_COMPATIBILITY_MODE': null,
            'CU1_SEND_PRICE_CATALOG': null,
            'CU1_PRICE_CATALOG_FORMAT': null,
            'CU1_ORDER_FORWARDING_FOLDER': null,
            'CU1_PRICE_CATALOG_IMPORT_FOLDER': null,
            'CU1_INVOICE_IMPORT_FOLDER': null,
            'CU1_PICKUP_EBAY': null,
            'CU1_EBAY_TOKEN': null,
            'CU1_EBAY_DEV_NAME': null,
            'CU1_EBAY_APP_NAME': null,
            'CU1_EBAY_CERT_NAME': null,
            'CU1_SEND_EDI_PICK_TICKETS': null,
            'CU1_PICK_TICKET_FORMAT': null,
            'CU1_PICK_TICKET_LOCATION_ID': null,
            'CU1_SAVE_867_IN_DATABASE': null,
            'CU1_SEND_ORDER_CHECK': null,
            'CU1_ORDER_CHECK_EMAIL': null,
            'CU1_SHIP_TO_FILTER': null,
            'CU1_REMOTE_FTP_DIRECTORY_SEND_INVENTORY': null,
            'CU1_PICKUP_AMAZON': null,
            'CU1_AMAZON_MWS_MARKET_ID_US': null,
            'CU1_AWS_ACCESS_KEY': null,
            'CU1_AWS_SECRET_KEY': null,
            'CU1_AWS_SELLER_ID': null,
            'CU1_AWS_MARKETPLACE_ID': null,
            'CU1_AWS_UTC_HOUR_OFFSET': null,
            'CU1_PICKUP_BILLTRUST': null,
            'CU1_BILLTRUST_DEVELOPER_GUID': null,
            'CU1_BILLTRUST_CLIENT_GUID': null,
            'CU1_QUOTE_FORMAT': null,
            'CU1_SEND_EDI_QUOTES': null,
            'CU1_FTP_DOWNLOAD_FILTER': null,
            'CU1_CHECK_ACKNOWLEDGEMENTS': null,
            'CU1_830_FORWARDING_FOLDER': null,
            'CU1_PICKUP_CHANNEL_ADVISOR': null,
            'CU1_CHANNEL_ADVISOR_DEV_KEY': null,
            'CU1_CHANNEL_ADVISOR_PASSWORD': null,
            'CU1_CHANNEL_ADVISOR_ACCOUNT_ID': null,
            'CU1_CHANNEL_ADVISOR_LOCAL_ID': null,
            'CU1_SFTP_PRIVATE_KEY_FILENAME': null,
            'CU1_ALWAYS_SEND_INVOICES': null,
            'CU1_QUOTE_IMPORT_FOLDER': null,
            'CU1_SEND_SINGLE_LINE_INVOICES': null,
            'CU1_INVOICE_FORWARDING_FOLDER': null,
            'CU1_PICKUP_EMAIL': null,
            'CU1_EMAIL_SERVER': null,
            'CU1_EMAIL_USER': null,
            'CU1_EMAIL_PASSWORD': null,
            'CU1_EMAIL_PORT': null,
            'CU1_EMAIL_REQUIRES_TLS': null,
            'CU1_PICKUP_SELLER_CLOUD': null,
            'CU1_SELLER_CLOUD_USERNAME': null,
            'CU1_SELLER_CLOUD_PASSWORD': null,
            'CU1_SELLER_CLOUD_PICKUP_FBA': null,
            'CU1_ASN_FORWARDING_FOLDER': null,
            'CU1_SEND_CONTRACT_ORDERS': null,
            'CU1_SEND_EDI_TRANSFERS': null,
            'CU1_TRANSFER_FORMAT': null,
            'CU1_TRANSFER_LOCATION_ID': null,
            'CU1_SEND_INVENTORY_RECEIPT_ORDERS': null,
            'CU1_SEND_EDI_ROUTING_REQUESTS': null,
            'CU1_ROUTING_REQUEST_FORMAT': null,
            'CU1_PICKUP_MAGENTO': null,
            'CU1_MAGENTO_VERSION': null,
            'CU1_MAGENTO_USERNAME': null,
            'CU1_MAGENTO_PASSWORD': null,
            'CU1_MAGENTO_API_KEY': null,
            'CU1_ORDER_CONFIRMATION_IMPORT_FOLDER': null,
            'CU1_RECEIVE_API_CALLS': null,
            'CU1_API_USERNAME': null,
            'CU1_API_PASSWORD': null,
            'CU1_PICKUP_CTL_SHIPMENTS': null,
            'CU1_CTL_CLIENT_ID': null,
            'CU1_CTL_USER_ID': null,
            'CU1_IMPORT_SCHEDULED_RELEASES_INTO_P21': null,
            'CU1_PICKUP_CTL_RMA_INFO': null,
            'CU1_SYNCHRONIZED': null,
            'CU1_PICKUP_BIG_COMMERCE': null,
            'CU1_BIG_COMMERCE_ACCESS_TOKEN': null,
            'CU1_BIG_COMMERCE_CLIENT_ID': null,
            'CU1_BIG_COMMERCE_CLIENT_SECRET': null,
            'CU1_REMOTE_ORDER_DATABASE': null,
            'CU1_REMOTE_ORDER_TABLE': null,
            'CU1_CUSTOMER_LIST_URL': null
        };
    }

    getStore() {
        return this.store;
    }

    preloadData(): Observable<any> {
        return this.http.get(this.apiUrl + '/customers/preload');
    }
	
	public getShortCustomerList(co1_id: number) : Observable<any> {
		return this.http.get(this.apiUrl + '/customer/short-customer-list',{params: {CO1_ID: co1_id.toString()}});
	}

}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/services/customer.service.ts